import type { NpcId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import { grantRewards } from './rewards';
import { dayKeyOf } from './time';

export type FriendshipSource = 'order' | 'shoot' | 'talk' | 'favoriteBuy' | 'pet' | 'pate' | 'dialogue';

type NpcState = GameSave['npcs'][NpcId];
type DailyNpc = GameSave['daily']['perNpc'][NpcId];

export function npcState(draft: GameSave, npcId: NpcId): NpcState {
  return (draft.npcs[npcId] ??= {
    friendship: 0,
    level: 0,
    talkCount: 0,
    lastDialogueId: null,
    pendingVisits: [],
    activeVisits: [],
  });
}

/** New calendar day → fresh daily caps. A clock moved backwards keeps today's counters (no extra quota). */
export function ensureDay(draft: GameSave, nowWallMs: number): void {
  const today = dayKeyOf(nowWallMs);
  if (today > draft.daily.dayKey) draft.daily = { dayKey: today, perNpc: {} };
}

function dailyOf(draft: GameSave, npcId: NpcId): DailyNpc {
  return (draft.daily.perNpc[npcId] ??= {
    talkBonusGiven: false,
    favoriteBuyPoints: 0,
    petPoints: 0,
    pateBonusGiven: false,
  });
}

/**
 * content-schemas §14.10: add friendship points with the daily caps, then level up (possibly more than once)
 * and grant each level's rewards exactly once (`fr:<npc>:L<n>`).
 */
export function addFriendship(
  draft: GameSave,
  content: ContentRegistry,
  npcId: NpcId,
  source: FriendshipSource,
  nowWallMs: number,
  amount?: number,
): DomainEvent[] {
  const def = content.npcs.get(npcId);
  if (!def) return [];
  ensureDay(draft, nowWallMs);
  const d = dailyOf(draft, npcId);
  const P = content.balance.friendshipPoints;
  let pts = 0;
  switch (source) {
    case 'order':
      pts = amount ?? P.order;
      break;
    case 'shoot':
      pts = amount ?? content.balance.studio.friendship;
      break;
    case 'talk':
      if (!d.talkBonusGiven) {
        d.talkBonusGiven = true;
        pts = P.firstTalkDaily;
      }
      break;
    case 'favoriteBuy':
      pts = Math.max(0, Math.min(amount ?? P.favoriteBuy, P.favoriteBuyDailyCap - d.favoriteBuyPoints));
      d.favoriteBuyPoints += pts;
      break;
    case 'pet':
      pts = Math.max(0, Math.min(P.catPet, P.catPetDailyCap - d.petPoints));
      d.petPoints += pts;
      break;
    case 'pate':
      if (!d.pateBonusGiven) {
        d.pateBonusGiven = true;
        pts = P.catPateDaily;
      }
      break;
    case 'dialogue':
      pts = amount ?? 0;
      break;
  }
  if (pts <= 0) return [];
  const n = npcState(draft, npcId);
  n.friendship += pts;
  const events: DomainEvent[] = [
    { type: 'FRIENDSHIP_CHANGED', npcId, delta: pts, total: n.friendship, source },
  ];
  const thresholds = content.balance.friendshipThresholds[def.thresholdsRef];
  while (n.level < thresholds.length && n.friendship >= (thresholds[n.level] ?? Infinity)) {
    n.level += 1;
    const rewards = def.levels.find((l) => l.level === n.level)?.rewards ?? [];
    events.push(...grantRewards(draft, content, `fr:${npcId}:L${String(n.level)}`, rewards));
    events.push({ type: 'FRIENDSHIP_LEVEL_REACHED', npcId, level: n.level, rewards });
  }
  return events;
}
